import React, { useEffect, useMemo, useRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import {
  fetchPublicApplicationLogById,
  fetchPublicApplicationLogs,
} from "../api/applicationLogsApi";
import {
  getBrowserFromUserAgent,
  getDeviceFromUserAgent,
} from "../utils/parseUserAgent";
import "../styles/applicationLogsPublicPage.css";

const LIMITS = [100, 200, 300];

/** Gate for the public logs console (session only). Credentials from frontend `.env`. */
const LOGS_AUTH_STORAGE_KEY = "girinestham.publicAppLogs.authenticated";
const LOGS_HARDCODED_USER = String(
  process.env.REACT_APP_PUBLIC_LOGS_USER || "",
).trim();
const LOGS_HARDCODED_PASSWORD = String(
  process.env.REACT_APP_PUBLIC_LOGS_PASSWORD || "",
);

const levelClass = (level) => {
  const v = String(level || "").toUpperCase();
  if (v.includes("ERROR")) return "alogs-pill alogs-pill--error";
  if (v.includes("WARN")) return "alogs-pill alogs-pill--warn";
  if (v.includes("SUCCESS")) return "alogs-pill alogs-pill--success";
  return "alogs-pill alogs-pill--info";
};

const fmt = (v) => (v == null || v === "" ? "-" : String(v));
const fmtTruncate = (v, maxLen = 100) => {
  const text = fmt(v);
  if (text === "-" || text.length <= maxLen) return text;
  return `${text.slice(0, maxLen - 3)}...`;
};
const fmtUserDisplay = (row) => {
  if (row.user_id == null || row.user_id === "") return "-";
  const name = row.user_name != null ? String(row.user_name).trim() : "";
  return name || `User #${row.user_id}`;
};
const fmtAdminDisplay = (row) => {
  if (row.admin_id == null || row.admin_id === "") return "-";
  const name = row.admin_name != null ? String(row.admin_name).trim() : "";
  return name || `Admin #${row.admin_id}`;
};
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const REFRESH_MS = 10000;

const COLUMN_VISIBILITY_KEY = "girinestham.publicAppLogs.columnVisibility";
const FILTER_COLUMNS_KEY = "girinestham.publicAppLogs.filterColumns";

/** Columns that get a filter dropdown by default (others are sort-only unless enabled). */
const DEFAULT_FILTER_COLUMN_KEYS = [
  "log_level",
  "log_type",
  "http_method",
  "status_code",
  "browser",
  "device",
];

/** Matches tbl_application_logs columns (lines 4–18). */
const TABLE_COLUMNS = [
  { key: "id", label: "ID", width: "72px", defaultVisible: true, getValue: (r) => fmt(r.id) },
  {
    key: "log_level",
    label: "Level",
    width: "95px",
    defaultVisible: true,
    getValue: (r) => fmt(r.log_level),
  },
  {
    key: "log_type",
    label: "Type",
    width: "95px",
    defaultVisible: true,
    getValue: (r) => fmt(r.log_type),
  },
  {
    key: "http_method",
    label: "Method",
    width: "90px",
    defaultVisible: true,
    getValue: (r) => fmt(r.http_method),
  },
  {
    key: "endpoint",
    label: "Endpoint",
    width: "260px",
    defaultVisible: true,
    getValue: (r) => fmt(r.endpoint),
  },
  {
    key: "user_id",
    label: "User",
    width: "140px",
    defaultVisible: false,
    getValue: fmtUserDisplay,
  },
  {
    key: "admin_id",
    label: "Admin",
    width: "180px",
    defaultVisible: false,
    getValue: fmtAdminDisplay,
  },
  {
    key: "status_code",
    label: "Status",
    width: "85px",
    defaultVisible: true,
    getValue: (r) => fmt(r.status_code),
  },
  {
    key: "message",
    label: "Message",
    width: "280px",
    defaultVisible: true,
    getValue: (r) => fmt(r.message),
    truncate: 120,
  },
  {
    key: "details",
    label: "Details",
    width: "220px",
    defaultVisible: false,
    getValue: (r) => fmt(r.details),
    truncate: 100,
  },
  {
    key: "stack_trace",
    label: "Stack Trace",
    width: "220px",
    defaultVisible: false,
    getValue: (r) => fmt(r.stack_trace),
    truncate: 100,
  },
  {
    key: "response_time_ms",
    label: "Resp(ms)",
    width: "95px",
    defaultVisible: true,
    getValue: (r) => fmt(r.response_time_ms),
  },
  {
    key: "ip_address",
    label: "IP",
    width: "140px",
    defaultVisible: true,
    getValue: (r) => fmt(r.ip_address),
  },
  {
    key: "user_agent",
    label: "User Agent",
    width: "280px",
    defaultVisible: false,
    getValue: (r) => fmt(r.user_agent),
    truncate: 80,
  },
  {
    key: "browser",
    label: "Browser",
    width: "130px",
    defaultVisible: true,
    getValue: (r) => getBrowserFromUserAgent(r.user_agent),
  },
  {
    key: "device",
    label: "Device",
    width: "180px",
    defaultVisible: true,
    getValue: (r) => getDeviceFromUserAgent(r.user_agent),
  },
  {
    key: "created_at",
    label: "Created At",
    width: "160px",
    defaultVisible: true,
    getValue: (r) => fmt(r.created_at),
  },
];

/** Filterable table columns (alias kept for compatibility). */
const FILTERABLE_COLUMNS = TABLE_COLUMNS;

const buildDefaultColumnVisibility = () =>
  Object.fromEntries(
    TABLE_COLUMNS.map((col) => [col.key, col.defaultVisible !== false]),
  );

function loadColumnVisibility() {
  const defaults = buildDefaultColumnVisibility();
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return defaults;
    return TABLE_COLUMNS.reduce((acc, col) => {
      acc[col.key] =
        typeof parsed[col.key] === "boolean" ? parsed[col.key] : defaults[col.key];
      return acc;
    }, {});
  } catch {
    return defaults;
  }
}

function buildDefaultFilterColumns() {
  return Object.fromEntries(
    TABLE_COLUMNS.map((col) => [
      col.key,
      DEFAULT_FILTER_COLUMN_KEYS.includes(col.key),
    ]),
  );
}

function loadFilterColumns() {
  const defaults = buildDefaultFilterColumns();
  try {
    const raw = localStorage.getItem(FILTER_COLUMNS_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return defaults;
    return TABLE_COLUMNS.reduce((acc, col) => {
      acc[col.key] =
        typeof parsed[col.key] === "boolean" ? parsed[col.key] : defaults[col.key];
      return acc;
    }, {});
  } catch {
    return defaults;
  }
}

function todayIsoDate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function renderCell(row, column) {
  if (column.key === "log_level") {
    return <span className={levelClass(row.log_level)}>{fmt(row.log_level)}</span>;
  }
  const full = column.getValue(row);
  const display = column.truncate ? fmtTruncate(full, column.truncate) : full;
  return display;
}

function toCreatedAtDateKey(value) {
  if (value == null || value === "") return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatCreatedAtDateLabel(dateKey) {
  if (!dateKey || !/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return dateKey || "-";
  const d = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateKey;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function getColumnFilterValue(column, row) {
  if (column.key === "created_at") return toCreatedAtDateKey(row.created_at);
  return column.getValue(row);
}

function uniqSortedValues(rows, getValue) {
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    const value = getValue(row);
    const key = String(value);
    if (!key || key === "-" || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

function rowMatchesFilters(row, filters, columns = FILTERABLE_COLUMNS) {
  const cols = Array.isArray(columns) ? columns : FILTERABLE_COLUMNS;
  if (!cols.length) return true;
  return cols.every((col) => {
    const selected = filters[col.key];
    if (!selected) return true;
    return getColumnFilterValue(col, row) === selected;
  });
}

function ColumnVisibilityPicker({ visibility, onChange }) {
  const pickerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const visibleCount = TABLE_COLUMNS.filter((c) => visibility[c.key]).length;

  useEffect(() => {
    const onDocClick = (event) => {
      if (!pickerRef.current?.contains(event.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggleColumn = (key) => {
    onChange((prev) => {
      const nextOn = prev[key] === false;
      const visibleCount = TABLE_COLUMNS.filter((c) => {
        if (c.key === key) return nextOn;
        return prev[c.key] !== false;
      }).length;
      if (visibleCount === 0) return prev;
      return { ...prev, [key]: nextOn };
    });
  };

  const showAll = () => {
    onChange(Object.fromEntries(TABLE_COLUMNS.map((col) => [col.key, true])));
  };

  const resetDefaults = () => onChange(buildDefaultColumnVisibility());

  return (
    <div className="alogs-column-picker" ref={pickerRef}>
      <button
        type="button"
        className="alogs-btn alogs-btn--ghost"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="alogs-column-panel"
      >
        Columns ({visibleCount}/{TABLE_COLUMNS.length})
      </button>
      {open ? (
        <div
          id="alogs-column-panel"
          className="alogs-column-picker__panel"
          role="group"
          aria-label="Choose visible columns"
        >
          <p className="alogs-column-picker__title">Show columns</p>
          <ul className="alogs-column-picker__list">
            {TABLE_COLUMNS.map((col) => (
              <li key={col.key}>
                <label className="alogs-column-picker__row">
                  <input
                    type="checkbox"
                    checked={visibility[col.key] !== false}
                    onChange={() => toggleColumn(col.key)}
                  />
                  <span>{col.label}</span>
                </label>
              </li>
            ))}
          </ul>
          <div className="alogs-column-picker__actions">
            <button type="button" className="alogs-btn alogs-btn--ghost" onClick={showAll}>
              Show all
            </button>
            <button type="button" className="alogs-btn alogs-btn--ghost" onClick={resetDefaults}>
              Reset
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FilterColumnsPicker({ filterColumns, onChange }) {
  const pickerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const enabledCount = TABLE_COLUMNS.filter((c) => filterColumns[c.key]).length;

  useEffect(() => {
    const onDocClick = (event) => {
      if (!pickerRef.current?.contains(event.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggleColumn = (key) => {
    onChange((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="alogs-column-picker" ref={pickerRef}>
      <button
        type="button"
        className="alogs-btn alogs-btn--ghost"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="alogs-filter-column-panel"
      >
        Filter columns ({enabledCount})
      </button>
      {open ? (
        <div
          id="alogs-filter-column-panel"
          className="alogs-column-picker__panel"
          role="group"
          aria-label="Choose columns with filter dropdowns"
        >
          <p className="alogs-column-picker__title">Dropdown filters</p>
          <p className="alogs-column-picker__hint">
            Only checked columns show the ▾ filter menu. Sorting still works on all columns.
          </p>
          <ul className="alogs-column-picker__list">
            {TABLE_COLUMNS.map((col) => (
              <li key={col.key}>
                <label className="alogs-column-picker__row">
                  <input
                    type="checkbox"
                    checked={Boolean(filterColumns[col.key])}
                    onChange={() => toggleColumn(col.key)}
                  />
                  <span>{col.label}</span>
                </label>
              </li>
            ))}
          </ul>
          <div className="alogs-column-picker__actions">
            <button
              type="button"
              className="alogs-btn alogs-btn--ghost"
              onClick={() =>
                onChange(Object.fromEntries(TABLE_COLUMNS.map((c) => [c.key, false])))
              }
            >
              None
            </button>
            <button
              type="button"
              className="alogs-btn alogs-btn--ghost"
              onClick={() => onChange(buildDefaultFilterColumns())}
            >
              Reset
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const DETAIL_BLOCK_KEYS = new Set([
  "endpoint",
  "message",
  "details",
  "stack_trace",
  "user_agent",
]);

function renderDetailValue(log, col) {
  if (col.key === "log_level") {
    return <span className={levelClass(log.log_level)}>{fmt(log.log_level)}</span>;
  }
  return col.getValue(log);
}

function LogDetailModal({ log, loading, onClose }) {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="alogs-modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="alogs-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="alogs-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="alogs-modal__header">
          <div>
            <h2 id="alogs-modal-title" className="alogs-modal__title">
              Log details
            </h2>
            <p className="alogs-modal__subtitle">
              {log ? `#${log.id}` : "Loading..."}
              {log?.created_at ? ` · ${fmt(log.created_at)}` : ""}
            </p>
          </div>
          <button
            type="button"
            className="alogs-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="alogs-modal__body">
          {loading && !log ? (
            <p className="alogs-modal__loading">Loading log details...</p>
          ) : log ? (
            <dl className="alogs-modal__grid">
              {TABLE_COLUMNS.map((col) => {
                const isWide = DETAIL_BLOCK_KEYS.has(col.key);
                return (
                  <div
                    key={col.key}
                    className={`alogs-modal__field${isWide ? " alogs-modal__field--wide" : ""}`}
                  >
                    <dt>{col.label}</dt>
                    <dd className={isWide ? "alogs-modal__value--block" : undefined}>
                      {renderDetailValue(log, col)}
                    </dd>
                  </div>
                );
              })}
            </dl>
          ) : (
            <p className="alogs-modal__loading">Could not load log details.</p>
          )}
        </div>

        <footer className="alogs-modal__footer">
          <button type="button" className="alogs-btn" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}

function compareColumnValues(a, b) {
  const aEmpty = a == null || a === "" || a === "-";
  const bEmpty = b == null || b === "" || b === "-";
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  const aNum = Number(a);
  const bNum = Number(b);
  if (Number.isFinite(aNum) && Number.isFinite(bNum) && String(a).trim() !== "" && String(b).trim() !== "") {
    if (aNum !== bNum) return aNum - bNum;
  }

  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function ColumnFilterHeader({
  column,
  rows,
  activeValue,
  isOpen,
  sortDir,
  filterEnabled,
  onToggle,
  onSelect,
  onSortCycle,
}) {
  const wrapRef = useRef(null);
  const options = useMemo(
    () => uniqSortedValues(rows, (row) => getColumnFilterValue(column, row)),
    [rows, column],
  );
  const showFilterDropdown =
    filterEnabled && (column.key !== "created_at" || options.length > 1);

  useEffect(() => {
    if (!showFilterDropdown && isOpen) onToggle(false);
  }, [showFilterDropdown, isOpen, onToggle]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (!wrapRef.current?.contains(event.target)) onToggle(false);
    };
    if (isOpen) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isOpen, onToggle]);

  const sortTitle =
    sortDir === "asc"
      ? "Sorted ascending — click for descending"
      : sortDir === "desc"
        ? "Sorted descending — click to clear"
        : "Click to sort ascending";

  return (
    <th style={{ width: column.width }} className="alogs-th-filter-wrap" ref={wrapRef}>
      <div
        className={`alogs-th-filter${activeValue ? " alogs-th-filter--active" : ""}${isOpen ? " alogs-th-filter--open" : ""}${sortDir ? " alogs-th-filter--sorted" : ""}`}
      >
        <button
          type="button"
          className="alogs-th-filter__label"
          onClick={onSortCycle}
          title={sortTitle}
        >
          <span>{column.label}</span>
          {sortDir === "asc" ? (
            <span className="alogs-th-filter__sort" aria-hidden>
              ↑
            </span>
          ) : null}
          {sortDir === "desc" ? (
            <span className="alogs-th-filter__sort" aria-hidden>
              ↓
            </span>
          ) : null}
        </button>
        {showFilterDropdown ? (
          <button
            type="button"
            className="alogs-th-filter__caret-btn"
            onClick={() => onToggle(!isOpen)}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-label={`Filter ${column.label}`}
            title="Filter column"
          >
            <span className="alogs-th-filter__caret" aria-hidden>
              ▾
            </span>
          </button>
        ) : null}
      </div>
      {isOpen && showFilterDropdown ? (
        <div className="alogs-th-filter__menu" role="listbox">
          <button
            type="button"
            className={`alogs-th-filter__option${!activeValue ? " alogs-th-filter__option--selected" : ""}`}
            onClick={() => onSelect("")}
          >
            All
          </button>
          {options.map((value) => {
            const label =
              column.key === "created_at" ? formatCreatedAtDateLabel(value) : value;
            return (
              <button
                key={value}
                type="button"
                className={`alogs-th-filter__option${activeValue === value ? " alogs-th-filter__option--selected" : ""}`}
                onClick={() => onSelect(value)}
                title={label}
              >
                {label}
              </button>
            );
          })}
        </div>
      ) : null}
    </th>
  );
}

function LogsLoginGate({ onAuthenticated }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!LOGS_HARDCODED_USER || !LOGS_HARDCODED_PASSWORD) {
      setLoginError(
        "Login is not configured. Set REACT_APP_PUBLIC_LOGS_USER and REACT_APP_PUBLIC_LOGS_PASSWORD in frontend .env.",
      );
      return;
    }
    const u = String(username || "").trim();
    const p = String(password || "");
    if (u === LOGS_HARDCODED_USER && p === LOGS_HARDCODED_PASSWORD) {
      setSubmitting(true);
      try {
        sessionStorage.setItem(LOGS_AUTH_STORAGE_KEY, "1");
      } catch {
        /* ignore */
      }
      setLoginError("");
      onAuthenticated();
      return;
    }
    setLoginError("Invalid username or password.");
  };

  return (
    <div className="alogs-login-screen">
      <div className="alogs-login-screen__atmosphere" aria-hidden>
        <div className="alogs-login-screen__grid" />
        <div className="alogs-login-screen__glow alogs-login-screen__glow--a" />
        <div className="alogs-login-screen__glow alogs-login-screen__glow--b" />
        <div className="alogs-login-screen__scan" />
      </div>

      <div className="alogs-login-screen__compose">
        <form className="alogs-login-panel" onSubmit={handleSubmit} noValidate>
          <label className="alogs-login-panel__field">
            <span className="alogs-login-panel__label">Username</span>
            <input
              type="text"
              autoComplete="username"
              autoFocus
              value={username}
              placeholder="Enter username"
              onChange={(e) => {
                setUsername(e.target.value);
                if (loginError) setLoginError("");
              }}
            />
          </label>
          <label className="alogs-login-panel__field">
            <span className="alogs-login-panel__label">Password</span>
            <div className="alogs-login-panel__password">
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                placeholder="Enter password"
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (loginError) setLoginError("");
                }}
              />
              <button
                type="button"
                className="alogs-login-panel__eye"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
              </button>
            </div>
          </label>
          {loginError ? (
            <p className="alogs-login-panel__error" role="alert">
              {loginError}
            </p>
          ) : null}
          <button
            type="submit"
            className="alogs-login-panel__submit"
            disabled={submitting}
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function ApplicationLogsPublicPage() {
  const [authenticated, setAuthenticated] = useState(() => {
    try {
      return sessionStorage.getItem(LOGS_AUTH_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedFrom, setAppliedFrom] = useState("");
  const [appliedTo, setAppliedTo] = useState("");
  const [dateError, setDateError] = useState("");
  const [limit, setLimit] = useState(100);
  const [page, setPage] = useState(1);
  const [periodRows, setPeriodRows] = useState([]);
  const [periodMeta, setPeriodMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastSync, setLastSync] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [openFilterColumn, setOpenFilterColumn] = useState("");
  const [sortColumnKey, setSortColumnKey] = useState("");
  const [sortDir, setSortDir] = useState(""); // "" | "asc" | "desc"
  const [errorCardFilter, setErrorCardFilter] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState(loadColumnVisibility);
  const [filterColumns, setFilterColumns] = useState(loadFilterColumns);

  const rangeApplied = Boolean(appliedFrom && appliedTo);

  const visibleColumns = useMemo(() => {
    if (!columnVisibility || typeof columnVisibility !== "object") {
      return TABLE_COLUMNS.filter((col) => col.defaultVisible !== false);
    }
    return TABLE_COLUMNS.filter((col) => columnVisibility[col.key] !== false);
  }, [columnVisibility]);
  const tableColCount = visibleColumns.length + 1;

  useEffect(() => {
    try {
      localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(columnVisibility));
    } catch {
      /* ignore */
    }
  }, [columnVisibility]);

  useEffect(() => {
    try {
      localStorage.setItem(FILTER_COLUMNS_KEY, JSON.stringify(filterColumns));
    } catch {
      /* ignore */
    }
  }, [filterColumns]);

  useEffect(() => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const key of Object.keys(next)) {
        if (columnVisibility[key] === false || !filterColumns[key]) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setOpenFilterColumn((prev) =>
      prev && (columnVisibility[prev] === false || !filterColumns[prev]) ? "" : prev,
    );
    setSortColumnKey((prev) =>
      prev && columnVisibility[prev] === false ? "" : prev,
    );
  }, [columnVisibility, filterColumns]);

  const loadPeriod = async (showLoader = true, from = appliedFrom, to = appliedTo) => {
    if (!from || !to) return;
    if (showLoader) setLoading(true);
    setError("");
    try {
      const result = await fetchPublicApplicationLogs({ fromDate: from, toDate: to });
      setPeriodRows(result.rows);
      setPeriodMeta(result.meta);
      setLastSync(new Date().toLocaleTimeString());
    } catch (err) {
      setError(err?.message || "Failed to load logs");
      setPeriodRows([]);
      setPeriodMeta(null);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const applyDateRange = () => {
    setDateError("");
    if (!fromDate || !toDate) {
      setDateError("Both From date and To date are required.");
      return;
    }
    if (fromDate > toDate) {
      setDateError("From date must be on or before To date.");
      return;
    }
    setAppliedFrom(fromDate);
    setAppliedTo(toDate);
    setPage(1);
    setColumnFilters({});
    setOpenFilterColumn("");
    setSortColumnKey("");
    setSortDir("");
    setErrorCardFilter(false);
  };

  useEffect(() => {
    if (!authenticated || !appliedFrom || !appliedTo) return undefined;
    loadPeriod(true, appliedFrom, appliedTo);
  }, [authenticated, appliedFrom, appliedTo]);

  useEffect(() => {
    if (!authenticated || !autoRefresh || !appliedFrom || !appliedTo) return undefined;
    const t = setInterval(() => {
      loadPeriod(false, appliedFrom, appliedTo);
    }, REFRESH_MS);
    return () => clearInterval(t);
  }, [autoRefresh, authenticated, appliedFrom, appliedTo]);

  useEffect(() => {
    setPage(1);
  }, [limit]);

  const hasActiveFilters = useMemo(
    () =>
      Boolean(sortColumnKey) ||
      errorCardFilter ||
      visibleColumns.some((col) => Boolean(columnFilters[col.key])),
    [visibleColumns, columnFilters, sortColumnKey, errorCardFilter],
  );

  const columnFilteredRows = useMemo(
    () =>
      periodRows.filter((row) =>
        rowMatchesFilters(
          row,
          columnFilters,
          visibleColumns.length ? visibleColumns : FILTERABLE_COLUMNS,
        ),
      ),
    [periodRows, columnFilters, visibleColumns],
  );

  const filteredRows = useMemo(() => {
    if (!errorCardFilter) return columnFilteredRows;
    return columnFilteredRows.filter((r) =>
      String(r.log_level || "").toUpperCase().includes("ERROR"),
    );
  }, [columnFilteredRows, errorCardFilter]);

  const sortedRows = useMemo(() => {
    if (!sortColumnKey || !sortDir) return filteredRows;
    const column = TABLE_COLUMNS.find((c) => c.key === sortColumnKey);
    if (!column) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const cmp = compareColumnValues(column.getValue(a), column.getValue(b));
      return sortDir === "desc" ? -cmp : cmp;
    });
  }, [filteredRows, sortColumnKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / limit) || 1);
  const safePage = Math.min(page, totalPages);
  const displayRows = useMemo(() => {
    const start = (safePage - 1) * limit;
    return sortedRows.slice(start, start + limit);
  }, [sortedRows, safePage, limit]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const applyColumnFilter = (columnKey, value) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (!value) delete next[columnKey];
      else next[columnKey] = value;
      return next;
    });
    setOpenFilterColumn("");
    setPage(1);
  };

  const cycleColumnSort = (columnKey) => {
    setOpenFilterColumn("");
    if (sortColumnKey !== columnKey) {
      setSortColumnKey(columnKey);
      setSortDir("asc");
      return;
    }
    if (sortDir === "asc") {
      setSortDir("desc");
      return;
    }
    if (sortDir === "desc") {
      setSortColumnKey("");
      setSortDir("");
      return;
    }
    setSortColumnKey(columnKey);
    setSortDir("asc");
  };

  const toggleErrorCardFilter = () => {
    setErrorCardFilter((prev) => !prev);
    setOpenFilterColumn("");
    setPage(1);
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setOpenFilterColumn("");
    setSortColumnKey("");
    setSortDir("");
    setErrorCardFilter(false);
    setPage(1);
  };

  const handleLogout = () => {
    try {
      sessionStorage.removeItem(LOGS_AUTH_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setAuthenticated(false);
    setPeriodRows([]);
    setPeriodMeta(null);
    setAppliedFrom("");
    setAppliedTo("");
    setSelectedLog(null);
    setDetailModalOpen(false);
    setColumnFilters({});
    setOpenFilterColumn("");
    setSortColumnKey("");
    setSortDir("");
    setErrorCardFilter(false);
  };

  const closeLogDetails = () => {
    setDetailModalOpen(false);
    setSelectedLog(null);
    setDetailLoading(false);
  };

  const openLogDetails = async (id) => {
    setDetailModalOpen(true);
    setSelectedLog(null);
    setDetailLoading(true);
    setError("");
    try {
      const row = await fetchPublicApplicationLogById(id);
      setSelectedLog(row);
    } catch (err) {
      setError(err?.message || "Failed to load log details");
      setDetailModalOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const lastUpdated = useMemo(
    () =>
      filteredRows[0]?.created_at
        ? new Date(filteredRows[0].created_at).toLocaleString()
        : periodRows[0]?.created_at
          ? new Date(periodRows[0].created_at).toLocaleString()
          : "-",
    [filteredRows, periodRows],
  );

  const metrics = useMemo(() => {
    if (!columnFilteredRows.length) {
      return {
        total: 0,
        errors: 0,
        warnings: 0,
        p95Ms: 0,
        avgMs: 0,
        successRate: 0,
      };
    }
    const errors = columnFilteredRows.filter((r) =>
      String(r.log_level || "").toUpperCase().includes("ERROR"),
    ).length;
    const warnings = columnFilteredRows.filter((r) =>
      String(r.log_level || "").toUpperCase().includes("WARN"),
    ).length;
    const responses = columnFilteredRows.map((r) => toNum(r.response_time_ms)).filter((n) => n > 0);
    const sorted = [...responses].sort((a, b) => a - b);
    const p95Idx = sorted.length ? Math.floor(sorted.length * 0.95) - 1 : -1;
    const p95Ms = p95Idx >= 0 ? sorted[p95Idx] : 0;
    const avgMs = responses.length
      ? Math.round(responses.reduce((s, n) => s + n, 0) / responses.length)
      : 0;
    const nonError = columnFilteredRows.length - errors;
    const successRate = Math.round((nonError / columnFilteredRows.length) * 100);
    return { total: columnFilteredRows.length, errors, warnings, p95Ms, avgMs, successRate };
  }, [columnFilteredRows]);

  const levelDistribution = useMemo(() => {
    const counts = filteredRows.reduce((acc, r) => {
      const key = String(r.log_level || "INFO").toUpperCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const max = Math.max(1, ...Object.values(counts));
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, count]) => ({ name, count, pct: Math.round((count / max) * 100) }));
  }, [filteredRows]);

  const dailyTrend = useMemo(() => {
    if (!appliedFrom || !appliedTo) return [];
    const map = new Map();
    const start = new Date(`${appliedFrom}T00:00:00`);
    const end = new Date(`${appliedTo}T00:00:00`);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      map.set(toCreatedAtDateKey(d), 0);
    }
    filteredRows.forEach((r) => {
      const key = toCreatedAtDateKey(r.created_at);
      if (map.has(key)) map.set(key, map.get(key) + 1);
    });
    const max = Math.max(1, ...map.values());
    return Array.from(map.entries()).map(([dateKey, count]) => ({
      label: formatCreatedAtDateLabel(dateKey),
      count,
      h: Math.max(8, Math.round((count / max) * 90)),
    }));
  }, [filteredRows, appliedFrom, appliedTo]);

  if (!authenticated) {
    return <LogsLoginGate onAuthenticated={() => setAuthenticated(true)} />;
  }

  return (
    <div className="alogs-page">
      <div className="alogs-shell">
        <div className="alogs-header">
          <div>
            <h1 className="alogs-title">Application Logs Console</h1>
            <p className="alogs-sub">
              Standalone log viewer (public URL)
              {rangeApplied
                ? ` — Period: ${appliedFrom} → ${appliedTo} — Latest: ${lastUpdated} — Sync: ${lastSync || "-"}`
                : " — Select From and To dates to load logs"}
            </p>
          </div>
          <div className="alogs-actions">
            <label className="alogs-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                disabled={!rangeApplied}
              />
              Live
            </label>
            <select
              className="alogs-limit"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              disabled={!rangeApplied}
            >
              {LIMITS.map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
            <button
              type="button"
              className="alogs-btn"
              onClick={() => loadPeriod(true)}
              disabled={loading || !rangeApplied}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
            <button
              type="button"
              className="alogs-btn alogs-btn--ghost"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </div>

        <div className="alogs-date-bar">
          <label className="alogs-date-field">
            <span>From date *</span>
            <input
              type="date"
              value={fromDate}
              max={toDate || undefined}
              onChange={(e) => {
                setFromDate(e.target.value);
                if (dateError) setDateError("");
              }}
            />
          </label>
          <label className="alogs-date-field">
            <span>To date *</span>
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              max={todayIsoDate()}
              onChange={(e) => {
                setToDate(e.target.value);
                if (dateError) setDateError("");
              }}
            />
          </label>
          <button type="button" className="alogs-btn" onClick={applyDateRange} disabled={loading}>
            Apply dates
          </button>
          <button
            type="button"
            className="alogs-btn alogs-btn--ghost"
            onClick={() => {
              const t = todayIsoDate();
              setFromDate(t);
              setToDate(t);
              setDateError("");
            }}
          >
            Today
          </button>
          {dateError ? (
            <p className="alogs-date-bar__error" role="alert">
              {dateError}
            </p>
          ) : null}
          {periodMeta?.capped ? (
            <p className="alogs-date-bar__warn">
              Showing first {periodMeta.returned} of {periodMeta.total} logs in this period
              (cap {periodMeta.maxRows}).
            </p>
          ) : null}
        </div>

        {!rangeApplied ? (
          <div className="alogs-empty-period">
            <h2>Choose a date range</h2>
            <p>
              From date and To date are required. Logs, filters, stats, and sorting apply only
              after you apply a period.
            </p>
          </div>
        ) : (
          <>
        <div className="alogs-metrics">
          <div className="alogs-card">
            <span className="alogs-card-label">Total in period</span>
            <strong>{metrics.total}</strong>
          </div>
          <button
            type="button"
            className={`alogs-card alogs-card--button${errorCardFilter ? " alogs-card--active" : ""}`}
            onClick={toggleErrorCardFilter}
            aria-pressed={errorCardFilter}
            title={
              errorCardFilter
                ? "Clear error filter"
                : "Show only error logs for this period"
            }
          >
            <span className="alogs-card-label">Errors in period</span>
            <strong>{metrics.errors}</strong>
          </button>
          <div className="alogs-card">
            <span className="alogs-card-label">Warning Logs</span>
            <strong>{metrics.warnings}</strong>
          </div>
          <div className="alogs-card">
            <span className="alogs-card-label">Success Rate</span>
            <strong>{metrics.successRate}%</strong>
          </div>
          <div className="alogs-card">
            <span className="alogs-card-label">Avg Resp Time</span>
            <strong>{metrics.avgMs} ms</strong>
          </div>
          <div className="alogs-card">
            <span className="alogs-card-label">P95 Resp Time</span>
            <strong>{metrics.p95Ms} ms</strong>
          </div>
        </div>

        <div className="alogs-graphs">
          <section className="alogs-graph-card">
            <h3>Level Distribution (period)</h3>
            <div className="alogs-bars">
              {levelDistribution.length === 0 ? (
                <p className="alogs-table-toolbar__hint">No data for this period</p>
              ) : (
                levelDistribution.map((it) => (
                  <div key={it.name} className="alogs-bar-row">
                    <div className="alogs-bar-label">{it.name}</div>
                    <div className="alogs-bar-track">
                      <div className="alogs-bar-fill" style={{ width: `${it.pct}%` }} />
                    </div>
                    <div className="alogs-bar-value">{it.count}</div>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className="alogs-graph-card">
            <h3>Logs per day (period)</h3>
            <div className="alogs-columns">
              {dailyTrend.length === 0 ? (
                <p className="alogs-table-toolbar__hint">No data for this period</p>
              ) : (
                dailyTrend.map((pt) => (
                  <div key={pt.label} className="alogs-col-wrap" title={`${pt.label} - ${pt.count} logs`}>
                    <div className="alogs-col" style={{ height: `${pt.h}px` }} />
                    <span className="alogs-col-count">{pt.count}</span>
                    <span className="alogs-col-label">{pt.label}</span>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>

        {error ? <div className="alogs-error">{error}</div> : null}

        <div className="alogs-table-toolbar">
          <ColumnVisibilityPicker
            visibility={columnVisibility}
            onChange={setColumnVisibility}
          />
          <FilterColumnsPicker
            filterColumns={filterColumns}
            onChange={setFilterColumns}
          />
          {hasActiveFilters ? (
            <div className="alogs-filter-bar alogs-filter-bar--inline">
              <span>
                Showing {sortedRows.length} of {periodRows.length} logs in period
                {periodMeta?.total != null && periodMeta.total !== periodRows.length
                  ? ` (loaded ${periodRows.length} / ${periodMeta.total})`
                  : ""}
              </span>
              <button type="button" className="alogs-btn alogs-btn--ghost" onClick={clearAllFilters}>
                Clear filters
              </button>
            </div>
          ) : (
            <span className="alogs-table-toolbar__hint">
              {visibleColumns.length} columns · period filters &amp; sort
            </span>
          )}
        </div>

        <div className="alogs-table-wrap">
          <table className="alogs-table">
            <thead>
              <tr>
                {visibleColumns.length === 0 ? (
                  <th colSpan={1} style={{ textAlign: "center", padding: "0.75rem" }}>
                    Select at least one column to display the table
                  </th>
                ) : (
                  visibleColumns.map((col) => (
                    <ColumnFilterHeader
                      key={col.key}
                      column={col}
                      rows={periodRows}
                      activeValue={columnFilters[col.key] || ""}
                      isOpen={openFilterColumn === col.key}
                      sortDir={sortColumnKey === col.key ? sortDir : ""}
                      filterEnabled={Boolean(filterColumns[col.key])}
                      onToggle={(nextOpen) =>
                        setOpenFilterColumn(nextOpen ? col.key : "")
                      }
                      onSelect={(value) => applyColumnFilter(col.key, value)}
                      onSortCycle={() => cycleColumnSort(col.key)}
                    />
                  ))
                )}
                {visibleColumns.length > 0 ? (
                  <th style={{ width: "90px" }}>View</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {visibleColumns.length === 0 ? null : periodRows.length === 0 ? (
                <tr>
                  <td colSpan={tableColCount} style={{ textAlign: "center", padding: "1rem" }}>
                    {loading ? "Loading logs..." : "No logs found in this date range"}
                  </td>
                </tr>
              ) : displayRows.length === 0 ? (
                <tr>
                  <td colSpan={tableColCount} style={{ textAlign: "center", padding: "1rem" }}>
                    No logs match the selected filters for this period.
                  </td>
                </tr>
              ) : (
                displayRows.map((r) => (
                  <tr key={r.id}>
                    {visibleColumns.map((col) => {
                      const full = col.getValue(r);
                      return (
                        <td key={col.key} title={col.truncate && full !== "-" ? full : undefined}>
                          {renderCell(r, col)}
                        </td>
                      );
                    })}
                    <td>
                      <button
                        type="button"
                        className="alogs-btn"
                        onClick={() => openLogDetails(r.id)}
                        disabled={detailLoading}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div
          style={{
            marginTop: "12px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            justifyContent: "space-between",
          }}
        >
          <div style={{ color: "#475569", fontSize: "13px" }}>
            Page {safePage} of {totalPages} · {sortedRows.length} filtered rows
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <button
              type="button"
              className="alogs-btn"
              disabled={loading || safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <button
              type="button"
              className="alogs-btn"
              disabled={loading || safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
          </>
        )}

      </div>

      {detailModalOpen ? (
        <LogDetailModal
          log={selectedLog}
          loading={detailLoading}
          onClose={closeLogDetails}
        />
      ) : null}
    </div>
  );
}
